/**
 * MetricCard - Reusable component for displaying a metric with label and optional change
 * Can be used in dashboards, summaries, and reports
 */

import React from 'react';

import { METRIC_ABSENT, formatMetric, isAbsent } from '../../../utils/metricFormat';
import TrendIndicator from './TrendIndicator';

interface MetricItem {
  label: string;
  /** Null when the metric was not computed — rendered `—`, never `0` (TASK-872). */
  value: number | string | null;
  format?: 'percentage' | 'decimal' | 'currency' | 'number';
  decimals?: number;
  trend?: 'up' | 'down' | 'neutral';
  currency?: string;
}

export interface MetricCardProps {
  title?: string;
  label?: string;
  value?: string | number | null;
  metrics?: MetricItem[];
  change?: number;
  changeLabel?: string;
  icon?: React.ReactNode;
  status?: 'good' | 'warning' | 'critical' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  label,
  value,
  metrics,
  change,
  changeLabel,
  icon,
  status = 'neutral',
  size = 'md',
  className = '',
  onClick
}) => {
  // Size configurations
  const sizeConfig = {
    sm: {
      padding: 'p-3',
      labelText: 'text-xs',
      valueText: 'text-lg',
      iconSize: 'w-4 h-4'
    },
    md: {
      padding: 'p-4',
      labelText: 'text-sm',
      valueText: 'text-2xl',
      iconSize: 'w-5 h-5'
    },
    lg: {
      padding: 'p-6',
      labelText: 'text-base',
      valueText: 'text-3xl',
      iconSize: 'w-6 h-6'
    }
  };
  
  const config = sizeConfig[size];
  
  // Status colors for border
  const statusColors = {
    good: 'border-green-200 bg-green-50',
    warning: 'border-yellow-200 bg-yellow-50',
    critical: 'border-red-200 bg-red-50',
    neutral: 'border-gray-200 bg-white'
  };
  
  const statusClass = statusColors[status];
  
  // TASK-872: the null branch below used to sit AFTER `typeof val === 'string'` and under a
  // prop typed `number | string`, so it could never run. `metricFormat` owns the rule now, and
  // the sign follows the displayed value — `+0.00%` on a rounded zero was the reported defect.
  const formatValue = (
    val: number | string | null | undefined,
    format?: string,
    decimals = 2,
    currencyCode = 'CHF',
  ) => {
    if (isAbsent(val)) return METRIC_ABSENT;
    if (typeof val === 'string') return val;

    switch (format) {
      case 'percentage':
        return formatMetric(val, { kind: 'percent', decimals, signed: true });
      case 'decimal':
        return formatMetric(val, { kind: 'number', decimals });
      case 'currency':
        // Kept as its own Intl call rather than routed through `formatMetric`: this branch
        // wants min 0 / MAX 2 fraction digits, so `CHF 1'234'567.89` keeps its centimes while
        // a round number stays `CHF 0`. Forcing a single `decimals` rounded ClientsTab's Total
        // AUM. The null case is already handled by the guard above, which is what this branch
        // was missing.
        return new Intl.NumberFormat('de-CH', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
        }).format(val);
      case 'number':
      default:
        return val.toLocaleString('de-CH');
    }
  };
  
  // If metrics array is provided, render multi-metric card
  if (metrics && metrics.length > 0) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        {title && (
          <h4 className="text-sm font-medium text-gray-500 mb-2">{title}</h4>
        )}
        <div className="space-y-3">
          {metrics.map((metric, index) => (
            <div key={index} className="flex justify-between">
              <span className="text-sm text-gray-600">{metric.label}</span>
              {/* TASK-872: `trend` is supplied by the caller and is independent of the value,
                  so an absent metric with `trend: 'up'` rendered a GREEN dash — a direction on
                  a number that does not exist. Absence outranks the caller's arrow. */}
              <span className={`text-sm font-semibold ${
                isAbsent(metric.value) ? 'text-gray-400' :
                metric.trend === 'up' ? 'text-green-600' :
                metric.trend === 'down' ? 'text-red-600' :
                'text-gray-900'
              }`}>
                {formatValue(metric.value, metric.format, metric.decimals, metric.currency)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Single metric display (original behavior). `value || ''` printed an empty string for a
  // legitimate 0 and nothing at all for a null — TASK-872: absent and zero are different facts.
  const displayValue = isAbsent(value)
    ? METRIC_ABSENT
    : typeof value === 'number'
      ? value.toLocaleString('de-CH')
      : value;
  
  return (
    <div 
      className={`
        ${config.padding} 
        rounded-lg border 
        ${statusClass} 
        ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {icon && <span className={config.iconSize}>{icon}</span>}
            <p className={`${config.labelText} font-medium text-gray-600`}>
              {label || title}
            </p>
          </div>
          <p className={`${config.valueText} font-bold text-gray-900`}>
            {displayValue}
          </p>
          {change !== undefined && (
            <div className="mt-1 flex items-center gap-2">
              <TrendIndicator value={change} iconSize="sm" />
              {changeLabel && (
                <span className="text-xs text-gray-500">{changeLabel}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricCard;